import { cn } from '@virginexperiencedays/tailwind';
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
} from 'react';

type ExpandableProps = {
  isExpanded: boolean;
  expandedHeight: number;
} & HTMLAttributes<HTMLDivElement>;

export const CollapsibleListExpandable = forwardRef<HTMLDivElement, ExpandableProps>(
  ({ isExpanded, expandedHeight, children, className, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        data-testid="collapsible-list-expanded"
        className={cn(
          'overflow-hidden opacity-0 transition-all duration-300',
          isExpanded && 'opacity-1',
          className
        )}
        style={{ maxHeight: isExpanded ? expandedHeight : 0 }}
        {...rest}
      >
        {children}
      </div>
    );
  }
);
CollapsibleListExpandable.displayName = 'CollapsibleListExpandable';

export const CollapsibleList = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ children, className, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        data-testid="collapsible-list"
        className={cn('flex flex-col', className)}
        {...rest}
      >
        {children}
      </div>
    );
  }
);
CollapsibleList.displayName = 'CollapsibleList';

type CollapsibleListTriggerProps = {
  isExpanded: boolean;
  isExpandable: boolean;
  text?: [string, string];
  scrollToTop?: boolean;
  handleToggleExpanded: (props: { scrollToTop: boolean }) => void;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export const CollapsibleListTrigger = ({
  text,
  isExpanded,
  isExpandable,
  handleToggleExpanded,
  scrollToTop = true,
  onClick,
  disabled,
  className,
  ...props
}: CollapsibleListTriggerProps) => {
  const [collapsedText, expandedText] = text ?? ['Read More', 'Read Less'];
  const isDisabled = disabled ?? !isExpandable;

  return (
    <button
      onClick={(e) => {
        handleToggleExpanded({ scrollToTop });
        onClick?.(e);
      }}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      aria-expanded={isExpanded}
      className={cn(
        'text-brand hidden font-semibold leading-6 underline',
        isExpandable && 'inline-block',
        className
      )}
      {...props}
    >
      {isExpanded ? expandedText : collapsedText}
    </button>
  );
};

type UseCollapsibleList = <T>(props: {
  list: T[];
  lineClampCount: number;
  defaultExpanded?: boolean;
}) => {
  containerRef: MutableRefObject<HTMLDivElement>;
  expandableContentRef: MutableRefObject<HTMLDivElement>;
  isExpanded: boolean;
  expandedHeight: number;
  isExpandable: boolean;
  handleToggleExpanded: (params: { scrollToTop: boolean }) => void;
  truncatedList: T[];
  expandedList: T[];
};

export const useCollapsibleList: UseCollapsibleList = ({
  list = [],
  lineClampCount = 3,
  defaultExpanded = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const expandableContentRef = useRef<HTMLDivElement>(null);

  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [expandedHeight, setExpandedHeight] = useState(0);

  useEffect(() => {
    if (!expandableContentRef.current) return;
    setExpandedHeight(isExpanded ? expandableContentRef.current.scrollHeight : 0);
  }, [isExpanded]);

  // TODO LP-6997 this has been copied from useClampedText, should be refactored to use the same hook/logic
  const handleToggleExpanded = ({ scrollToTop }: { scrollToTop?: boolean }) => {
    let shouldScrollTop = false;
    if (containerRef.current) {
      const { top, height } = containerRef.current.getBoundingClientRect();
      const isAboveViewport = top < 0;
      const isLargerThanViewport = height > window.innerHeight;

      shouldScrollTop = isAboveViewport && isLargerThanViewport;
    }

    setIsExpanded((prev) => {
      if (containerRef.current && prev && scrollToTop && shouldScrollTop) {
        containerRef.current.scrollIntoView({
          block: 'start',
          behavior: 'instant' as ScrollBehavior,
        });
      }
      return !prev;
    });
  };

  return {
    containerRef,
    expandableContentRef,
    isExpanded,
    expandedHeight,
    handleToggleExpanded,
    isExpandable: list.length > lineClampCount,
    truncatedList: list.slice(0, lineClampCount),
    expandedList: list.slice(lineClampCount),
  };
};
